//angular imports
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  resource,
  Signal,
  signal,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";

//local imports
import { CatalogConstants, CatalogStateService, DocumentModel } from "@app/catalog";
import { CatalogCommonConstants } from "@app/catalog-common";
import { ApplicationSecurity, SecurityConfig } from "@app/core";
import { Messenger, UnityConfig, UnityParams, UnitySecurity } from "@app/shared";

@Component({
  selector: "room-property",
  standalone: false,
  templateUrl: "./room-property.component.html",
  styleUrl: "./room-property.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoomPropertyComponent {
  private route = inject(ActivatedRoute);
  private catalogStateService = inject(CatalogStateService);

  public readonly is3DViewerEnable = signal(false);
  public catalogConstants = CatalogConstants;

  public roomPropertyResource = resource({
    request: () => ({
      id: this.route.snapshot.paramMap.get("id"),
      departmentId: this.route.snapshot.queryParamMap.get(CatalogCommonConstants.ROUTE_PARAMS.DEPARTMENT_ID),
      programId: this.route.snapshot.queryParamMap.get(CatalogCommonConstants.ROUTE_PARAMS.PROGRAM_ID),
      projectId: this.route.snapshot.queryParamMap.get(CatalogCommonConstants.ROUTE_PARAMS.PROJECT_ID),
    }),
    loader: async ({ request }) => {
      const property = await this.catalogStateService.getRoomProperty(
        request.id,
        request.departmentId,
        request.programId,
        request.projectId,
      );

      if (property.documents) {
        const filteredDocuments = property.documents().filter(
          (doc: DocumentModel) => !doc.superseded()
        );
        property.documents.set(filteredDocuments);
      }
          
      return property;
    }
  });

  public RoomPropertyComponent: typeof RoomPropertyComponent = RoomPropertyComponent;


  public roomContents = computed(() => {
    return this.roomPropertyResource.value().categories().flatMap(cat => cat.roomContents());
  });
  
  public static cacheContext: any = {};

  public config = computed(() => {
    let c = new UnityConfig();

    c.dataUrl = "/assets/partviewer/Build/part-viewer.data.gz";
    c.frameworkUrl = "/assets/partviewer/Build/part-viewer.framework.js.gz";
    c.codeUrl = "/assets/partviewer/Build/part-viewer.wasm.gz";
    c.streamingAssetsUrl = "/assets/partviewer/StreamingAssets";
    c.productName = "Modutecture Part Viewer";
    return c;
  });

  public security = computed(() => {
    return {
      getProtectedResources: () => this.applicationSecurity.getProtectedResources(),
      applicationUri: this.securityConfig.applicationUri,
      appId: this.securityConfig.appId,
      azureB2c: {
          instance: this.securityConfig.azureB2c.instance,
          domain: this.securityConfig.azureB2c.domain
      }
    } as UnitySecurity });

  public params: Signal<UnityParams[]> = computed(() => {
    return [ 
      { key: "SetPartId", value: "00000000-0000-0000-0000-000000000000"  },
      { key: "SetComponentId", value: "00000000-0000-0000-0000-000000000000"  } ];
  });

  constructor(
    private applicationSecurity: ApplicationSecurity,
    private securityConfig: SecurityConfig,
    private messenger: Messenger
  ) {}

  public open3DViewer(): void {
    this.is3DViewerEnable.set(true);
  }
}
